import { Controller, useForm } from "react-hook-form";
import { InputPhone } from "../../../../components/common/InputPhone";
import React, { useCallback, useEffect, useState } from "react";
import { ContentWrapper, Error, InputWrapper, RateBox } from "../styles";
import CustomSelect from "../../../../components/common/CustomSelect";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import { BalanceExceptionWrapper } from "../../pages/Wallet/ChinaPayoutV2/styles";
import { ReactComponent as NotifyIcon } from "../../../../assets/icons/notify-icon.svg";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { Button } from "../../../../components/common/Button";
import { ghsPayoutMethods } from "../../const";
import { getPayoutTransferFee } from "../../redux/slice";
import { formatCurrencyAmount } from "../../../../utils/currency";
import Loader from "../../../../components/common/Loader";
import { savedPayoutV2Data } from "../../redux/formDataSlice";

type AmountType = {
  phoneNumber?: string;
  paymentMethod?: string;
  amount?: string | number;
  handleNext?: () => void;
  currency?: string;
  totalAmount?: number;
  balanceError?: boolean;
  fees?: string;
};

const SELECTED_PAYMENT_METHOD = "BANK_ACCOUNT";

const Amount = ({ handleNext }: AmountType) => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AmountType>();

  const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
  const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

  const dispatch = useAppDispatch();
  const paymentMethod = watch("paymentMethod");

  const INT_FORM_DATA = useAppSelector(
    (state) => state.formData?.payoutv2Data || {},
  );
  const [userData, setUserData] = useState<AmountType>(INT_FORM_DATA);

  const filteredWallets = wallets as AmountType[];

  const handleDataUpdate = (field: keyof AmountType, value: any) => {
    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const uniqueCurrencies = Array.from(
    new Set(filteredWallets?.map((wallet) => wallet.currency)),
  );

  const options = uniqueCurrencies.map((currency) => ({
    label: currency,
    value: currency,
  }));

  const walletToDebit = options?.filter((option) => option.value === "GHS");

  useEffect(() => {
    (async () => {
      const resultAction = await dispatch(getPayoutTransferFee("GH"));
      if (getPayoutTransferFee.fulfilled.match(resultAction)) {
        const { fees } = resultAction.payload;
        handleDataUpdate("fees", fees);
      }
    })();
  }, []);

  useEffect(() => {
    if (getSavedData) {
      Object.keys(getSavedData).forEach((key) => {
        setValue(
          key as keyof AmountType,
          getSavedData[key as keyof AmountType],
        );
      });
    }
  }, [getSavedData, setValue]);

  const handleNextStep = useCallback(
    (data: AmountType) => {
      const selectedBank = ghsPayoutMethods?.find(
        (bank) => bank.value === data.paymentMethod,
      );

      const combinedData = {
        ...userData,
        ...data,
        paymentMethodName: selectedBank?.label,
        paymentMethodValue: selectedBank?.value,
      };

      dispatch(savedPayoutV2Data(combinedData));
      handleNext?.();
    },
    [userData, ghsPayoutMethods],
  );

  useEffect(() => {
    if (userData.amount) {
      handleDataUpdate(
        "totalAmount",
        Number(userData.amount) + Number(userData.fees),
      );
    }
  }, [userData.amount]);

  return (
    <ContentWrapper>
      <div>
        <Controller
          name="paymentMethod"
          control={control}
          rules={{ required: "Payment method is required" }}
          render={({ field }) => {
            return (
              <CustomSelect
                {...field}
                options={ghsPayoutMethods}
                $width="100%"
                placeholder="Select payment method"
              />
            );
          }}
        />
        {<Error>{errors?.paymentMethod?.message}</Error>}
      </div>
      {paymentMethod !== SELECTED_PAYMENT_METHOD && (
        <InputWrapper>
          <Controller
            name="phoneNumber"
            control={control}
            rules={{ required: "Beneficiary's phone number is required" }}
            render={({ field }) => (
              <InputPhone
                label="Phone number"
                placeholder="Enter phone number"
                field={field}
                defaultCountry={"GH"}
              />
            )}
          />
          {<Error>{errors?.phoneNumber?.message}</Error>}
        </InputWrapper>
      )}

      <div>
        <Controller
          name="amount"
          control={control}
          rules={{ required: "Amount is required" }}
          defaultValue=""
          render={({ field }) => {
            return (
              <InputAndSelect
                amountValue={field.value as number}
                onAmountChange={(value) => {
                  field.onChange(value);
                  handleDataUpdate("amount", value);
                }}
                width="100%"
                placeholder="Amount"
                options={walletToDebit}
                selectValue={"GHS"}
              />
            );
          }}
        />
        {<Error>{errors?.amount?.message}</Error>}

        {userData?.balanceError && (
          <BalanceExceptionWrapper>
            <NotifyIcon /> <span>Exceeds your wallet balance.</span>
            <Link to="/wallets/ghs"> Top-up funds</Link>
          </BalanceExceptionWrapper>
        )}
      </div>

      <section>
        <RateBox>
          <span className="fee-box">
            +
            <span>
              {formatCurrencyAmount("GHS", userData?.fees ?? 0, 2) ?? 0}
            </span>
          </span>
          <span>Processing fees</span>
        </RateBox>
      </section>

      {userData.amount && (
        <section>
          <RateBox>
            <span className="fee-box">
              <span>
                {formatCurrencyAmount("GHS", userData.totalAmount, 2) ?? 0}
              </span>
            </span>
            <span>Total amount</span>
          </RateBox>
        </section>
      )}

      <Button
        height="48px"
        label="Continue"
        disabled={userData.balanceError || loading}
        onClick={handleSubmit(handleNextStep)}
      />

      <Loader isLoading={loading} />
    </ContentWrapper>
  );
};
export default Amount;
